import { Textarea, TextField, Tooltip } from '@ads/components-react';
import { DocumentTextIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';
import { Alert } from '@/shared/ui/atoms';
import { ConfirmDialog } from '@/shared/ui/molecules';

import type { FuturePaymentProvider } from '../../../hooks/useParcelFuturePayment';
import {
  INVOICE_BODY_MAX_LENGTH,
  useParcelFuturePayment,
} from '../../../hooks/useParcelFuturePayment';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import type { PlanWriteError } from '../../../utils/planWriteError';

/**
 * Formato mínimo de e-mail: algo, arroba, algo, ponto, algo — sem espaço.
 *
 * Não é validação de existência, e não tenta ser: o objetivo é pegar o erro de
 * digitação óbvio antes de gravar. Um e-mail errado aqui falha em silêncio — a
 * request é aceita, o endereço é gravado e o envio, que acontece em outra
 * camada, simplesmente não chega a ninguém.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Props de `ParcelFuturePaymentModal`. */
export interface ParcelFuturePaymentModalProps {
  /** Contrato da rota, para invalidar a grid de parcelas no sucesso. */
  readonly financialPersonId: number | null;
  /** Parcela alvo. `null` mantém o diálogo fechado. */
  readonly parcelId: number | null;
  /** A parcela, para o contexto no cabeçalho e os avisos de elegibilidade. */
  readonly parcel?: IndividualPaymentParcelViewModel | undefined;
  /** Total de parcelas do contrato, para o rótulo `3/12`. */
  readonly numberParcels?: number | null | undefined;
  /**
   * Parceiro emissor da NF antecipada. Muda o endpoint e o título; o formulário
   * é o mesmo. Padrão `fealq`.
   */
  readonly provider?: FuturePaymentProvider | undefined;
  readonly onClose: () => void;
  /** Chamado depois de o backend aceitar o pedido. */
  readonly onMarked: () => void;
}

/**
 * Diálogo de marcação de pagamento futuro de uma parcela.
 *
 * É a operação "Marcar pagamento futuro" do menu da parcela, sobre
 * `POST /individualpayment/parcels/{parcelId}/invoice-future-payment`. Reproduz o
 * diálogo do legado — a pergunta e a caixa de texto do corpo da NF — e acrescenta
 * duas coisas que o legado não tem:
 *
 * - **E-mail de envio.** O endpoint aceita `invoiceEmail` e o legado não expõe.
 *   Fica opcional, e o tooltip do ícone ao lado do rótulo diz o que acontece
 *   quando fica em branco: nada é gravado e nenhum e-mail sai. O rodapé do campo
 *   é reservado para o erro de formato, que precisa aparecer sem hover.
 * - **Elegibilidade antes do clique.** `preventFuturePayment` e `paymentFuture`
 *   já vêm na grid e não eram usados em nenhum lugar. Com eles, o operador lê o
 *   impedimento no diálogo em vez de descobrir pelo `400`.
 *
 * O corpo da NF é opcional, como no legado: confirmar sem texto manda a request
 * sem o parâmetro e deixa o backend com o seu padrão.
 *
 * Erros não fecham o diálogo — a mensagem do backend aparece dentro dele e o
 * operador corrige o texto sem perder o que digitou.
 */
export function ParcelFuturePaymentModal({
  financialPersonId,
  parcelId,
  parcel,
  numberParcels,
  provider = 'fealq',
  onClose,
  onMarked,
}: ParcelFuturePaymentModalProps) {
  const { t } = useTranslation('individualPayment');
  const emailFieldId = useId();
  const [invoiceBody, setInvoiceBody] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [failure, setFailure] = useState<PlanWriteError | null>(null);

  /**
   * Onde o tooltip é montado — o conteúdo do diálogo, não o `body`.
   *
   * O `ConfirmDialog` usa `<dialog>` com `showModal()`, que promove o elemento ao
   * **top layer**. O portal padrão do Radix monta no `body`, e o que está fora do
   * top layer é pintado **abaixo** dele por definição: o tooltip sairia atrás do
   * backdrop, invisível, e `z-index` não resolve (é o que diferencia este caso do
   * `DisciplineFormModal`, que usa um Dialog do Radix e por isso se acerta com
   * `z-[9999]`).
   *
   * É estado, não `ref`: o valor precisa provocar um render quando o nó existe,
   * senão o portal já teria capturado `null` e caído no `body`.
   */
  const [tooltipContainer, setTooltipContainer] = useState<HTMLElement | null>(null);

  const { mark, isPending } = useParcelFuturePayment(financialPersonId, parcelId, provider);

  const trimmedEmail = invoiceEmail.trim();
  const isEmailInvalid = trimmedEmail !== '' && !EMAIL_PATTERN.test(trimmedEmail);

  // O backend recusa e o aviso explica por quê, mas o botão fica ativo: a recusa
  // é regra do backend, e travar a ação aqui esconderia uma parcela que passou a
  // ser elegível sem a grid ter sido recarregada.
  const isPrevented = parcel?.preventFuturePayment === true;
  const isAlreadyMarked = parcel?.paymentFuture === true;

  // Fechado é desmontado, como no `ParcelActionConfirmModal`: o `<dialog>` nativo
  // não deixa estilo preso no `body` ao sair (era esse o problema do overlay do
  // Radix), e desmontar é o que descarta o texto digitado e o erro da tentativa
  // anterior sem um efeito de limpeza para manter em sincronia.
  if (parcelId === null) return null;

  const parcelLabel =
    parcel?.numberParcel == null
      ? EMPTY_VALUE
      : numberParcels == null
        ? t('parcels.table.parcelNumberShort', { number: parcel.numberParcel })
        : t('parcels.table.parcelNumber', { number: parcel.numberParcel, total: numberParcels });

  const handleConfirm = () => {
    if (isEmailInvalid) return;
    setFailure(null);

    mark(
      { invoiceBody, invoiceEmail },
      {
        onSuccess: () => {
          setInvoiceBody('');
          setInvoiceEmail('');
          onMarked();
        },
        onError: setFailure,
      },
    );
  };

  return (
    <ConfirmDialog
      open
      tone="brand"
      icon={DocumentTextIcon}
      title={
        provider === 'vanzolini'
          ? t('parcels.futurePayment.titleVanzolini')
          : t('parcels.futurePayment.title')
      }
      description={t('parcels.futurePayment.question')}
      confirmLabel={t('parcels.futurePayment.confirm')}
      cancelLabel={t('parcels.futurePayment.cancel')}
      closeAriaLabel={t('parcels.futurePayment.close')}
      isPending={isPending}
      confirmDisabled={isEmailInvalid}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div ref={setTooltipContainer} className="flex flex-col gap-xxxs">
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('parcels.futurePayment.context', {
            parcel: parcelLabel,
            date: formatContractDate(parcel?.expirationDate),
          })}
        </p>

        {isPrevented ? <Alert type="warning" title={t('parcels.futurePayment.prevented')} /> : null}

        {isAlreadyMarked ? (
          <Alert
            type="information"
            title={
              parcel?.futurePaymentDate
                ? t('parcels.futurePayment.alreadyMarked', {
                    date: formatContractDate(parcel.futurePaymentDate),
                  })
                : t('parcels.futurePayment.alreadyMarkedNoDate')
            }
          />
        ) : null}

        {failure !== null ? (
          <Alert
            type={failure.kind === 'server' ? 'danger' : 'warning'}
            title={
              failure.kind === 'server'
                ? t('parcels.futurePayment.serverError')
                : failure.kind === 'forbidden'
                  ? t('parcels.futurePayment.forbidden')
                  : failure.message === null
                    ? t('parcels.futurePayment.rejectedFallback')
                    : t('parcels.futurePayment.rejected', { message: failure.message })
            }
          />
        ) : null}

        {/* O `Textarea` do ADS em vez do atom do projeto: o wrapper simplifica as
            props e não expõe `label` nem `maxLength`, que são justo os dois de
            que este campo precisa — o rótulo do legado e o contador do limite. */}
        <Textarea
          label={t('parcels.futurePayment.bodyLabel')}
          optionalMessage={t('parcels.futurePayment.optional')}
          placeholder={t('parcels.futurePayment.bodyPlaceholder')}
          maxLength={INVOICE_BODY_MAX_LENGTH}
          rows={5}
          fullWidth
          value={invoiceBody}
          onChange={event => setInvoiceBody(event.target.value)}
          disabled={isPending}
        />

        {/* O rótulo é montado aqui em vez de vir do `label` do `TextField`: a
            prop é `string`, e o ícone de ajuda precisa ficar ao lado do texto.
            As classes replicam as do ADS (`ts-subtitle-xxs` sob
            `text-ctx-content-title`, `gap-quark` até o campo) para o rótulo sair
            igual ao do campo de cima, e o `htmlFor` refaz a associação que o
            `<label>` do próprio componente daria. */}
        <div className="flex w-full flex-col gap-quark text-ctx-content-title">
          <span className={`flex items-center gap-quark ${isPending ? 'opacity-medium' : ''}`}>
            <label htmlFor={emailFieldId} className="ts-subtitle-xxs">
              {t('parcels.futurePayment.emailLabel')} ({t('parcels.futurePayment.optional')})
            </label>

            <Tooltip
              title={t('parcels.futurePayment.emailHint')}
              innerProps={{ portal: { container: tooltipContainer } }}
            >
              {/* Trigger focável de propósito: num diálogo o operador navega por
                  teclado, e um tooltip só de hover esconderia a regra de quem
                  não usa mouse. O `aria-label` é o próprio texto da ajuda, então
                  focar o ícone já anuncia o conteúdo. */}
              <button
                type="button"
                aria-label={t('parcels.futurePayment.emailHint')}
                className="inline-flex cursor-help items-center"
              >
                <InformationCircleIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
              </button>
            </Tooltip>
          </span>

          <TextField
            id={emailFieldId}
            type="email"
            placeholder={t('parcels.futurePayment.emailPlaceholder')}
            helper={isEmailInvalid ? t('parcels.futurePayment.emailInvalid') : ''}
            hasError={isEmailInvalid}
            fullWidth
            value={invoiceEmail}
            onChange={event => setInvoiceEmail(event.target.value)}
            disabled={isPending}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
}
